import { countLabel } from 'utils/ru';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  useConnection,
  useConnections,
  useConnectorTypes,
  useCreateDirectSource,
  useDirectCatalog,
  useProbeDirect,
  useUpdateConnection,
} from 'api/connectors';
import { takeOAuthDraft, useOAuthGrants } from 'api/oauth';
import { useWarehouses } from 'api/warehouse';
import { extractApiError, localizeApiMessage } from 'api/errors';
import OAuthConnectField from 'components/common/OAuthConnectField';
import { t } from 'i18n';
import { useTranslation } from 'react-i18next';
import { translateMetadata } from 'i18n/metadata';

/*
 * Yandex Direct source wizard — an advertiser's own account, or an agency's
 * clients in one connection (a workspace holds one Direct connection).
 *
 *   1. Account  — a connected Yandex account holding direct:api, or a token.
 *   2. Clients  — the probe names the account; an agency lists its clients
 *                 with their daily API points. The ticked list is fixed at save.
 *   3. Streams  — statistics (free), objects (spend the client's points) and
 *                 dictionaries, from the static catalog.
 *   4. Settings — VAT, attribution model, lookback, the points ceiling, goals.
 *   5. Period   — the first day, an optional history load, destination, cadence.
 *
 * Nothing is saved until step 5. Edit mode (`/connectors/direct/:id/edit`)
 * reads the saved choices from `visible_config`, never re-types the token
 * (blank means keep), leaves VAT and the attribution model as saved (the
 * server freezes them once data is loaded) and PATCHes the connection alone.
 */

const STEPS = () => ([t('data.account'), t('data.clients'), t('data.whatToLoad'), t('data.settings'), t('data.periodAndPipeline')]);
const CONNECTOR_SLUG = 'yandex-direct';
const PROVIDER = 'yandex';

const CRON_PRESETS = [
  { get label() { return t('data.dailyAt3Am'); }, value: '0 3 * * *' },
  { get label() { return t('data.dailyAt7Am'); }, value: '0 7 * * *' },
  { get label() { return t('data.every6Hours'); }, value: '0 */6 * * *' },
  { get label() { return t('data.hourly'); }, value: '0 * * * *' },
];
const DEFAULT_CRON = '0 3 * * *';
// A month per history run, as the connector's run policy is set up.
const BACKFILL_SLICE_DAYS = 31;
// A big agency lists thousands of clients; the search narrows the rest.
const CLIENT_ROWS_SHOWN = 200;

const PROBE_ERROR_HINTS = {
  get auth() { return t('data.theTokenNeedsTheDirectApiPermissionReconnectTheAccountOr'); },
  get access() { return t('data.theAccountCannotUseTheDirectApiYetAcceptTheDirect'); },
  get app_access() { return t('data.yandexHasNotApprovedThePlatformSApplicationForTheDirect'); },
  get rate_limit() { return t('data.nothingIsWrongWithTheTokenTryAgainLater'); },
  get unreachable() { return t('data.thisIsRetryableTryAgainInAMoment'); },
  get config() { return t('data.checkTheTokenOrConnectAnAccountInstead'); },
  get grant_inactive() { return t('data.theConnectedAccountNoLongerWorksReconnectItOrConnectAnother'); },
};

const ACCOUNT_TYPES = { get AGENCY() { return t('data.agency'); }, get CLIENT() { return t('data.advertiser'); }, get SUBCLIENT() { return t('data.agencySClient'); } };
const VAT_LABELS = { get YES() { return t('data.withVat'); }, get NO() { return t('data.withoutVat'); } };
const ATTRIBUTION_LABELS = {
  get LC() { return t('data.lastClick'); },
  get LSCCD() { return t('data.lastSignificantClickCrossDevice'); },
  get FCCD() { return t('data.firstClickCrossDevice'); },
  get AUTO() { return t('data.automatic'); },
};
const UNITS_SOURCE_LABELS = {
  get client() { return t('data.theClientSOwnPoints'); },
  get client_then_agency() { return t('data.theClientSThenTheAgencyS'); },
  get agency() { return t('data.theAgencySPoints'); },
};

const GROUPS = [
  {
    kind: 'report',
    get title() { return t('data.statisticsNoApiPoints'); },
    get note() { return t('data.reportsCostNoApiPointsARowIsOneDayOf'); },
  },
  {
    kind: 'object',
    get title() { return t('data.objectsSpendTheClientSApiPoints'); },
    get note() { return t('data.aDailySnapshotOfWhatEachClientHasNowEveryRun'); },
  },
  {
    kind: 'dictionary',
    get title() { return t('data.dictionaries'); },
    get note() { return t('data.directSReferenceDataTheSameForEveryClientTakenOnce'); },
  },
];

const pad = (n) => String(n).padStart(2, '0');
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Direct keeps statistics from the first day of the month `months` back.
function earliestDay(months) {
  const now = new Date();
  return isoDay(new Date(now.getFullYear(), now.getMonth() - months, 1));
}

function daysBetween(from, to) {
  const start = Date.parse(from);
  const end = Date.parse(to);
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return 0;
  return Math.round((end - start) / 86_400_000);
}

const splitList = (raw) =>
  String(raw ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const wholeIn = (value, low, high) =>
  /^\d+$/.test(String(value).trim()) && Number(value) >= low && Number(value) <= high;

const formatPoints = (points) =>
  typeof points === 'number' ? t('data.pointsPerDay', { points: countLabel(points, 'point') }) : t('data.pointsUnknown');

function costLabel(stream) {
  if (stream.kind === 'dictionary') {
    return t('data.pointsPerRun', { points: countLabel(stream.units_per_call, 'point') });
  }
  if (stream.kind !== 'object') return '';
  const base = stream.units_per_object
    ? t('data.valueValuePerObjectPerRun', { value1: stream.units_per_call, value2: stream.units_per_object })
    : t('data.valuePerRun', { value1: stream.units_per_call });
  return stream.units_note ? `${base}, ${translateMetadata(stream.units_note)}` : base;
}

// Every message of a 400, nested ones included: the save answers on keys the
// wizard has no field for (connector_type_slug, config, pipeline, …).
function saveErrorText(exc) {
  const data = exc?.response?.status === 400 ? exc.response.data : null;
  if (!data || typeof data !== 'object') return extractApiError(exc);
  const messages = [];
  const walk = (value) => {
    if (typeof value === 'string') messages.push(localizeApiMessage(value));
    else if (Array.isArray(value)) value.forEach(walk);
    else if (value && typeof value === 'object') Object.values(value).forEach(walk);
  };
  walk(data);
  return messages.length ? [...new Set(messages)].join(' ') : extractApiError(exc);
}

function probeErrorText(failure) {
  return PROBE_ERROR_HINTS[failure.error] || localizeApiMessage(failure.hint, null, t('data.theCheckFailed'));
}

export default function DirectSourceWizardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { connectionId } = useParams();
  const editing = Boolean(connectionId);
  const probe = useProbeDirect();
  const createSource = useCreateDirectSource();
  const updateConnection = useUpdateConnection(connectionId);
  const { data: connection } = useConnection(connectionId);
  const { data: connections } = useConnections();
  const { data: types } = useConnectorTypes();
  const { data: grants } = useOAuthGrants(PROVIDER, CONNECTOR_SLUG);
  const { data: catalog, isLoading: catalogLoading } = useDirectCatalog();
  const { data: warehouses } = useWarehouses();

  const connectorType = useMemo(
    () => (types || []).find((t) => t.slug === CONNECTOR_SLUG) || null,
    [types],
  );
  const auth = connectorType?.auth?.kind === 'oauth' ? connectorType.auth : null;
  const oauthErrorCode = searchParams.get('oauth_error');
  // A workspace holds one Direct connection; the server refuses a second.
  const existing = useMemo(
    () =>
      editing
        ? null
        : (connections || []).find((c) => c.connector_type?.slug === CONNECTOR_SLUG) || null,
    [connections, editing],
  );

  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState(null);

  // Step 1 — account.
  const [grantId, setGrantId] = useState(null);
  const [authToken, setAuthToken] = useState('');

  // Step 2 — the probe's answer for THESE credentials, and the ticked clients.
  const [probeResult, setProbeResult] = useState(null);
  const [probeFailure, setProbeFailure] = useState(null);
  const probeSeq = useRef(0);
  const [logins, setLogins] = useState([]);
  const [clientFilter, setClientFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  // Step 3 — streams (the required ones are added on top).
  const [streams, setStreams] = useState([]);

  // Step 4 — settings; the catalog's defaults land once it loads.
  const [includeVat, setIncludeVat] = useState('YES');
  const [attributionModel, setAttributionModel] = useState('LC');
  const [lookbackDays, setLookbackDays] = useState('30');
  const [unitsCeiling, setUnitsCeiling] = useState('50');
  const [unitsSource, setUnitsSource] = useState('client');
  const [goalIds, setGoalIds] = useState('');

  // Step 5 — period, destination and cadence.
  const [connectionName, setConnectionName] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [cron, setCron] = useState(DEFAULT_CRON);
  const [startDate, setStartDate] = useState(() =>
    editing ? '' : isoDay(new Date(Date.now() - 86_400_000)),
  );
  const [backfill, setBackfill] = useState({ enabled: false, start: '', end: '' });

  // Edit mode: what is saved, so an addition can be told apart.
  const [savedLogins, setSavedLogins] = useState([]);
  const [savedStreams, setSavedStreams] = useState([]);

  // Back from the consent screen: the callback appends ?oauth_grant= (or
  // ?oauth_error=) and the page's state was parked in sessionStorage.
  const restored = useRef(false);
  const fromDraft = useRef(false);
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    const draft = takeOAuthDraft(PROVIDER);
    const grantParam = searchParams.get('oauth_grant');
    if (draft?.wizard === CONNECTOR_SLUG) {
      fromDraft.current = true;
      setLogins(draft.logins || []);
      setStreams(draft.streams || []);
      setIncludeVat(draft.includeVat || 'YES');
      setAttributionModel(draft.attributionModel || 'LC');
      setLookbackDays(draft.lookbackDays ?? '30');
      setUnitsCeiling(draft.unitsCeiling ?? '50');
      setUnitsSource(draft.unitsSource || 'client');
      setGoalIds(draft.goalIds || '');
      setConnectionName(draft.connectionName || '');
      if (draft.startDate !== undefined) setStartDate(draft.startDate);
    }
    if (grantParam) setGrantId(grantParam);
  }, [searchParams]);

  // Once the catalog is here: its defaults on create, the saved choices on edit.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !catalog || (editing && !connection)) return;
    seeded.current = true;
    if (!editing) {
      if (fromDraft.current) return;
      setStreams([...catalog.default_streams]);
      setIncludeVat(catalog.include_vat.default);
      setAttributionModel(catalog.attribution_model.default);
      setLookbackDays(String(catalog.lookback_days.default));
      setUnitsCeiling(String(catalog.units_ceiling_percent.default));
      setUnitsSource(catalog.units_source.default);
      return;
    }
    const saved = connection.visible_config || {};
    const known = new Set(catalog.streams.map((s) => s.name));
    const savedStreamNames = splitList(saved.streams).filter((name) => known.has(name));
    const savedLoginList = splitList(saved.client_logins).map((login) => login.toLowerCase());
    setSavedStreams(savedStreamNames);
    setSavedLogins(savedLoginList);
    // Read-only in edit mode, so always the saved value.
    setIncludeVat(saved.include_vat || catalog.include_vat.default);
    setAttributionModel(saved.attribution_model || catalog.attribution_model.default);
    const savedGrant = connection.oauth_grant?.public_id;
    if (savedGrant) setGrantId((current) => current || savedGrant);
    if (fromDraft.current) return;
    setConnectionName(connection.name || '');
    setStreams(savedStreamNames.length ? savedStreamNames : [...catalog.default_streams]);
    setLogins(savedLoginList);
    setLookbackDays(String(saved.lookback_days ?? catalog.lookback_days.default));
    setUnitsCeiling(String(saved.units_ceiling_percent ?? catalog.units_ceiling_percent.default));
    setUnitsSource(saved.units_source || catalog.units_source.default);
    setGoalIds(saved.goal_ids ? String(saved.goal_ids) : '');
    setStartDate(saved.start_date ? String(saved.start_date) : '');
  }, [catalog, connection, editing]);

  const activeWarehouses = useMemo(
    () => (warehouses || []).filter((w) => w.is_active),
    [warehouses],
  );

  // ── step 1 ─────────────────────────────────────────────────────────────────

  const selectedGrant = (grants || []).find((g) => g.public_id === grantId) || null;
  const grantLacksScopes = Boolean(selectedGrant?.missing_scopes?.length);
  const hasCredential = Boolean(grantId || authToken.trim() || editing);

  // Different credentials void the answer, including one still in flight.
  const forgetProbe = () => {
    probeSeq.current += 1;
    setProbeResult(null);
    setProbeFailure(null);
  };

  // How the probe reaches Direct: the saved connection in edit mode (unless a
  // new token was pasted), else the grant, else the typed token.
  const probeRoute = () => {
    if (editing && !authToken.trim() && !grantId) return { connectionId };
    if (grantId) return { grantId };
    return { config: { auth_token: authToken.trim() } };
  };

  const runProbe = async (refresh) => {
    probeSeq.current += 1;
    const seq = probeSeq.current;
    setProbeFailure(null);
    try {
      const result = await probe.mutateAsync({ ...probeRoute(), refresh });
      if (seq !== probeSeq.current) return;
      if (!result.reachable) {
        setProbeFailure(result);
        return;
      }
      setProbeResult(result);
      const { account } = result;
      setConnectionName((name) => name || t('data.yandexDirectValue', { value1: account.name || account.login }));
    } catch (exc) {
      if (seq !== probeSeq.current) return;
      setProbeFailure({ error: t('data.request'), hint: extractApiError(exc) });
    }
  };

  const next = () => {
    if (activeStep === 0 && !probeResult) runProbe(false);
    setActiveStep((s) => s + 1);
  };

  // ── step 2 ─────────────────────────────────────────────────────────────────

  const account = probeResult?.account || null;
  // Without an answer (edit mode, the list unavailable) the saved list decides.
  const isAgency = account ? account.type === 'AGENCY' : logins.length > 0;
  const clients = useMemo(() => probeResult?.clients || [], [probeResult]);
  const clientsByLogin = useMemo(() => new Map(clients.map((c) => [c.login, c])), [clients]);
  const loginSet = useMemo(() => new Set(logins), [logins]);
  const archivedCount = clients.filter((c) => c.archived).length;
  const activeLogins = clients.filter((c) => !c.archived).map((c) => c.login);
  const needle = clientFilter.trim().toLowerCase();
  const matching = useMemo(
    () =>
      clients.filter(
        (c) =>
          (showArchived || !c.archived || loginSet.has(c.login)) &&
          (!needle || c.login.includes(needle) || c.name.toLowerCase().includes(needle)),
      ),
    [clients, showArchived, loginSet, needle],
  );
  const shownClients = matching.slice(0, CLIENT_ROWS_SHOWN);
  // Ticked logins the agency does not list (any more).
  const strayLogins = probeResult && isAgency ? logins.filter((l) => !clientsByLogin.has(l)) : [];

  const toggleLogin = (login) =>
    setLogins((current) =>
      current.includes(login) ? current.filter((l) => l !== login) : [...current, login],
    );
  const selectAllActive = () =>
    setLogins((current) => [...current, ...activeLogins.filter((l) => !current.includes(l))]);

  // ── step 3 ─────────────────────────────────────────────────────────────────

  const required = useMemo(() => new Set(catalog?.required_streams || []), [catalog]);
  const streamSet = useMemo(() => new Set([...streams, ...required]), [streams, required]);
  const chosenStreams = (catalog?.streams || [])
    .filter((s) => streamSet.has(s.name))
    .map((s) => s.name);
  const queryDays = catalog?.search_query_days ?? 180;

  const toggleStream = (name) => {
    if (required.has(name)) return;
    setStreams((current) =>
      current.includes(name) ? current.filter((n) => n !== name) : [...current, name],
    );
  };
  const setGroup = (members, on) => {
    const names = members.map((s) => s.name);
    setStreams((current) =>
      on
        ? [...current, ...names.filter((n) => !current.includes(n))]
        : current.filter((n) => !names.includes(n)),
    );
  };

  const addedLogins = editing && isAgency ? logins.filter((l) => !savedLogins.includes(l)) : [];
  const addedStreams = editing ? chosenStreams.filter((n) => !savedStreams.includes(n)) : [];
  const reloadWarning = (addedLogins.length > 0 || addedStreams.length > 0) && (
    <Alert severity="warning" sx={{ mb: 2 }}>{t('data.newClientsOrStreamsNeedAHistoryReloadBackfillUntilThen')}{addedLogins.length > 0 && t('data.newClientsValue', { value1: addedLogins.join(', ') })}
      {addedStreams.length > 0 && t('data.newStreamsValue', { value1: addedStreams.join(', ') })}
    </Alert>
  );

  // ── step 4 ─────────────────────────────────────────────────────────────────

  const maxLookback = catalog?.lookback_days?.max ?? 90;
  const lookbackOk = wholeIn(lookbackDays, 0, maxLookback);
  const ceilingOk = wholeIn(unitsCeiling, 1, 100);
  const goalsOk = splitList(goalIds).every((goal) => /^\d+$/.test(goal));

  // ── step 5 ─────────────────────────────────────────────────────────────────

  const historyMonths = catalog?.history_months ?? 36;
  const earliest = earliestDay(historyMonths);
  const startOk = editing
    ? !startDate || startDate >= earliest
    : Boolean(startDate) && startDate >= earliest;
  const estimate = useMemo(
    () => daysBetween(backfill.start, backfill.end),
    [backfill.start, backfill.end],
  );
  const backfillOk = !backfill.enabled || (estimate > 0 && backfill.start >= earliest);
  const backfillRuns = Math.ceil(estimate / BACKFILL_SLICE_DAYS);
  const queryCutoff = isoDay(new Date(Date.now() - queryDays * 86_400_000));
  const queriesCutShort =
    backfill.enabled &&
    streamSet.has('search_query_stats') &&
    Boolean(backfill.start) &&
    backfill.start < queryCutoff;

  const saving = createSource.isPending || updateConnection.isPending;

  const connectionConfig = () => {
    const config = {
      // Blank on edit means "keep the stored token"; with an account the
      // server drops a typed token anyway.
      auth_token: grantId ? '' : authToken.trim(),
      // Lists, not joined strings: on edit a blank string means "keep the
      // saved value", while an empty list really clears it.
      client_logins: isAgency ? logins : [],
      streams: chosenStreams.join(','),
      start_date: startDate,
      lookback_days: Number(lookbackDays),
      units_ceiling_percent: Number(unitsCeiling),
      goal_ids: splitList(goalIds),
    };
    if (isAgency) config.units_source = unitsSource;
    // Frozen once data is loaded: edit mode leaves them as saved.
    if (!editing) {
      config.include_vat = includeVat;
      config.attribution_model = attributionModel;
    }
    return config;
  };

  const saveEdit = async () => {
    setError(null);
    try {
      // Switching from the connected account to a typed token detaches the
      // account; left attached, the server would drop the typed token.
      const detach = !grantId && Boolean(connection?.oauth_grant) && Boolean(authToken.trim());
      await updateConnection.mutateAsync({
        name: connectionName.trim(),
        config: connectionConfig(),
        ...(grantId ? { oauth_grant_id: grantId } : {}),
        ...(detach ? { oauth_grant_id: null } : {}),
      });
      navigate(`/console/connectors/${connectionId}`);
    } catch (exc) {
      setError(saveErrorText(exc));
    }
  };

  const save = async () => {
    if (editing) return saveEdit();
    setError(null);
    const payload = {
      connection: {
        name: connectionName.trim(),
        connector_type_slug: CONNECTOR_SLUG,
        config: connectionConfig(),
        ...(grantId ? { oauth_grant_id: grantId } : {}),
      },
      pipeline: {
        name: t('data.warehouseFlow', { name: connectionName.trim() }),
        warehouse_id: warehouseId,
        schedule_cron: cron,
      },
      ...(backfill.enabled && estimate
        ? {
            backfill: {
              start_date: backfill.start,
              end_date: backfill.end,
              slice_days: BACKFILL_SLICE_DAYS,
            },
          }
        : {}),
    };
    try {
      await createSource.mutateAsync(payload);
      navigate('/console/pipelines');
    } catch (exc) {
      setError(saveErrorText(exc));
    }
  };

  const canLeaveStep = [
    hasCredential && !grantLacksScopes,
    probeResult ? !isAgency || logins.length > 0 : editing && Boolean(probeFailure),
    chosenStreams.length > 0,
    lookbackOk && ceilingOk && goalsOk,
    Boolean(connectionName.trim() && (editing || warehouseId) && startOk && backfillOk),
  ];

  // The token is not parked: after the consent an account stands in for it.
  const draft = {
    wizard: CONNECTOR_SLUG,
    logins,
    streams,
    includeVat,
    attributionModel,
    lookbackDays,
    unitsCeiling,
    unitsSource,
    goalIds,
    connectionName,
    startDate,
  };
  const returnTo = editing
    ? `/console/connectors/direct/${connectionId}/edit`
    : '/console/connectors/new/direct';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton
          onClick={() =>
            navigate(editing ? `/console/connectors/${connectionId}` : '/console/connectors')
          }
          size="small"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">
          {editing ? connectionName || t('data.yandexDirectSource') : t('data.yandexDirectSource')}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{t('data.browsingCostsNoMoneyTheClientListSpendsTheAccountS')}{' '}
        {editing
          ? t('data.nothingChangesUntilYouSaveOnTheLastStep')
          : t('data.nothingIsSavedUntilTheLastStep')}
      </Typography>

      {existing && (
        <Alert
          severity="info"
          sx={{ mb: 3, maxWidth: 860 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate(`/console/connectors/direct/${existing.public_id}/edit`)}
            >{t('data.editIt')}</Button>
          }
        >{t('data.thisWorkspaceAlreadyHasAYandexDirectConnection')}{' '}<strong>{existing.name}</strong>{t('data.aWorkspaceHoldsOneAddTheOtherClientsToItInstead')}</Alert>
      )}

      <Stepper activeStep={activeStep} sx={{ mb: 4, maxWidth: 860 }}>
        {STEPS().map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3, maxWidth: 860 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {activeStep === 0 && (
        <Box sx={{ maxWidth: 720 }}>
          {auth && (
            <>
              <OAuthConnectField
                auth={auth}
                connectorTypeSlug={CONNECTOR_SLUG}
                grantId={grantId}
                onGrantChange={(value) => {
                  setGrantId(value);
                  forgetProbe();
                }}
                returnTo={returnTo}
                draft={draft}
                errorCode={oauthErrorCode}
              />
              <Divider sx={{ my: 2 }} />
            </>
          )}
          {!grantId && (
            <TextField
              label={editing ? t('data.oauthTokenLeaveBlankToKeep') : t('data.oauthToken')}
              type="password"
              helperText={
                editing
                  ? t('data.theStoredTokenNeverLeavesTheServerPasteANewOne')
                  : t('data.onlyWithoutAConnectedAccountATokenWithDirectApiObtained')
              }
              value={authToken}
              onChange={(e) => {
                setAuthToken(e.target.value);
                forgetProbe();
              }}
              fullWidth
              sx={{ mb: 2 }}
            />
          )}
          <Typography variant="caption" color="text.secondary" display="block">{t('data.directSApiAccessHasNoReadOnlyFormSoThe')}{' '}<code>get</code>{' '}{t('data.andTheReportsService')}</Typography>
        </Box>
      )}

      {activeStep === 1 && (
        <Box sx={{ maxWidth: 860 }}>
          {reloadWarning}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {account && (
              <>
                <Typography variant="subtitle2">{account.name || account.login}</Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={account.login}
                  sx={{ fontFamily: 'monospace' }}
                />
                <Chip size="small" label={ACCOUNT_TYPES[account.type] || account.type} />
              </>
            )}
            {probe.isPending && (
              <>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">{t('data.askingYandexDirectWhoThisTokenCanLoad')}</Typography>
              </>
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              disabled={probe.isPending || !hasCredential}
              onClick={() => runProbe(Boolean(probeResult))}
            >
              {probeResult ? t('data.refreshTheList') : t('data.tryAgain')}
            </Button>
          </Box>

          {probeFailure && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {probeErrorText(probeFailure)}
            </Alert>
          )}
          {!probeResult && editing && probeFailure && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {logins.length
                ? t('data.theSavedClientsStayAsTheyAreValue', { value1: logins.join(', ') })
                : t('data.theConnectionKeepsLoadingTheAccountSOwnCampaigns')}{' '}{t('data.theOtherStepsCanStillBeChanged')}</Alert>
          )}

          {account && !isAgency && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="body2">{t('data.theConnectionLoadsThisAccountSOwnCampaignsThereIsNothing')}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                <Chip size="small" variant="outlined" label={t('data.accountLogin', { name: account.login })} />
                {account.currency && (
                  <Chip size="small" variant="outlined" label={t('data.accountCurrency', { name: account.currency })} />
                )}
                <Chip size="small" variant="outlined" label={formatPoints(account.api_points)} />
              </Box>
            </Paper>
          )}

          {account && isAgency && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('data.tickTheClientsToLoadTheListIsFixedWhenYou')}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder={t('data.searchByLoginOrName')}
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                  sx={{ width: 280 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button size="small" onClick={selectAllActive} disabled={!activeLogins.length}>{t('data.selectAllActive')}</Button>
                <Button size="small" onClick={() => setLogins([])} disabled={!logins.length}>{t('data.clear')}</Button>
                <FormControlLabel
                  sx={{ ml: 0.5 }}
                  control={
                    <Switch
                      size="small"
                      checked={showArchived}
                      onChange={(e) => setShowArchived(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">{t('data.showArchived')}{archivedCount})</Typography>}
                />
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  {logins.length}{' '}{t('data.of')}{' '}{clients.length}{' '}{t('data.selected')}</Typography>
              </Box>
              <Paper variant="outlined" sx={{ borderRadius: 2, maxHeight: 440, overflow: 'auto' }}>
                {shownClients.map((client) => (
                  <Box
                    key={client.login}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 1,
                      py: 0.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={loginSet.has(client.login)}
                      onChange={() => toggleLogin(client.login)}
                      inputProps={{ 'aria-label': client.login }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {client.login}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {client.name}
                      </Typography>
                    </Box>
                    {client.archived && <Chip size="small" label={t('data.archived')} />}
                    {client.currency && (
                      <Chip size="small" variant="outlined" label={client.currency} />
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ width: 150, textAlign: 'right', flexShrink: 0 }}
                    >
                      {formatPoints(client.api_points)}
                    </Typography>
                  </Box>
                ))}
                {!shownClients.length && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {clients.length ? t('data.noClientMatches') : t('data.theAgencyHasNoClientsYet')}
                  </Typography>
                )}
              </Paper>
              {matching.length > shownClients.length && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>{t('data.showing')}{' '}{shownClients.length}{' '}{t('data.of')}{' '}{matching.length}{' '}{t('data.narrowTheSearchToSeeTheRest')}</Typography>
              )}
              {strayLogins.length > 0 && (
                <Alert
                  severity="warning"
                  sx={{ mt: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => setLogins((current) => current.filter((l) => clientsByLogin.has(l)))}
                    >{t('data.remove')}</Button>
                  }
                >{t('data.theAgencyDoesNotList')}{' '}{strayLogins.join(', ')}{' '}{t('data.now')}</Alert>
              )}
            </>
          )}
        </Box>
      )}

      {activeStep === 2 && (
        <Box sx={{ maxWidth: 860 }}>
          {reloadWarning}
          {catalogLoading && <CircularProgress size={20} />}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('data.eachStreamBecomesOneTableInTheWarehouseLoadedADay')}</Typography>
          {GROUPS.map((group) => {
            const members = (catalog?.streams || []).filter((s) => s.kind === group.kind);
            if (!members.length) return null;
            const picked = members.filter((s) => streamSet.has(s.name)).length;
            return (
              <Paper key={group.kind} variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 240 }}>
                    <Typography variant="subtitle2">{group.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {group.note}
                    </Typography>
                  </Box>
                  <Chip size="small" variant="outlined" label={t('data.valueOfValue', { value1: picked, value2: members.length })} />
                  <Button size="small" onClick={() => setGroup(members, true)}>{t('data.selectAll')}</Button>
                  <Button size="small" onClick={() => setGroup(members, false)}>{t('data.clear')}</Button>
                </Box>
                {members.map((stream) => {
                  const cost = costLabel(stream);
                  const always = required.has(stream.name);
                  return (
                    <Box
                      key={stream.name}
                      sx={{
                        px: 2,
                        py: 1,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={streamSet.has(stream.name)}
                        disabled={always}
                        onChange={() => toggleStream(stream.name)}
                        inputProps={{ 'aria-label': stream.name }}
                        sx={{ mt: -0.5 }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" fontWeight={500}>
                            {translateMetadata(stream.title)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {stream.name}
                          </Typography>
                          {always && <Chip size="small" label={t('data.alwaysLoaded')} />}
                          {stream.name === 'search_query_stats' && (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={t('data.directKeepsQueriesValueDays', { value1: queryDays })}
                            />
                          )}
                          {cost && <Chip size="small" variant="outlined" label={cost} />}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {translateMetadata(stream.description)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Paper>
            );
          })}
        </Box>
      )}

      {activeStep === 3 && (
        <Box sx={{ maxWidth: 720 }}>
          <Typography variant="subtitle2" gutterBottom>{t('data.money')}</Typography>
          <RadioGroup row value={includeVat} onChange={(e) => setIncludeVat(e.target.value)}>
            {(catalog?.include_vat?.choices || ['YES', 'NO']).map((choice) => (
              <FormControlLabel
                key={choice}
                value={choice}
                disabled={editing}
                control={<Radio size="small" />}
                label={<Typography variant="body2">{VAT_LABELS[choice] || choice}</Typography>}
              />
            ))}
          </RadioGroup>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3 }}>{t('data.spendCostPerClickAndRevenueWithOrWithoutVatFixed')}</Typography>
          <TextField
            select
            label={t('data.attributionModel')}
            value={attributionModel}
            onChange={(e) => setAttributionModel(e.target.value)}
            disabled={editing}
            fullWidth
            sx={{ mb: 3 }}
            helperText={t('data.whichClickAConversionIsCreditedToFixedOnceDataIs')}
          >
            {(catalog?.attribution_model?.choices || [attributionModel]).map((choice) => (
              <MenuItem key={choice} value={choice}>
                {choice} — {ATTRIBUTION_LABELS[choice] || choice}
              </MenuItem>
            ))}
          </TextField>
          {editing && (
            <Alert severity="info" sx={{ mb: 3 }}>{t('data.vatAndTheAttributionModelAreFixedOnceDataIsLoaded')}</Alert>
          )}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              type="number"
              label={t('data.lookbackDays')}
              value={lookbackDays}
              onChange={(e) => setLookbackDays(e.target.value)}
              error={!lookbackOk}
              inputProps={{ min: 0, max: maxLookback }}
              helperText={t('data.eachRunReReadsThisManyDaysDirectRevisesClicksAnd', { value1: maxLookback })}
              sx={{ flex: 1, minWidth: 260 }}
            />
            <TextField
              type="number"
              label={t('data.apiPointsCeiling')}
              value={unitsCeiling}
              onChange={(e) => setUnitsCeiling(e.target.value)}
              error={!ceilingOk}
              inputProps={{ min: 1, max: 100 }}
              helperText={t('data.theShareOfAClientSAvailableApiPointsOneRun')}
              sx={{ flex: 1, minWidth: 260 }}
            />
          </Box>
          {isAgency && (
            <TextField
              select
              label={t('data.whoseApiPoints')}
              value={unitsSource}
              onChange={(e) => setUnitsSource(e.target.value)}
              fullWidth
              sx={{ mb: 3 }}
              helperText={t('data.whosePointsTheObjectStreamsOfAnAgencySClientSpend')}
            >
              {(catalog?.units_source?.choices || [unitsSource]).map((choice) => (
                <MenuItem key={choice} value={choice}>
                  {UNITS_SOURCE_LABELS[choice] || choice}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            label={t('data.extraMetricaGoalsOptional')}
            value={goalIds}
            onChange={(e) => setGoalIds(e.target.value)}
            error={!goalsOk}
            fullWidth
            helperText={
              !goalsOk
                ? t('data.goalIdsAreNumbersSeparatedByCommas')
                : t('data.goalIdsForConversionsByGoalBesidesTheCampaignsKeyGoals')
            }
          />
        </Box>
      )}

      {activeStep === 4 && (
        <Box sx={{ maxWidth: 720 }}>
          {reloadWarning}
          <TextField
            label={t('data.connectionName')}
            value={connectionName}
            onChange={(e) => setConnectionName(e.target.value)}
            required
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            type="date"
            label={t('data.startLoadingFrom')}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required={!editing}
            error={!startOk}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: earliest }}
            fullWidth
            sx={{ mb: 3 }}
            helperText={
              editing && !startDate
                ? t('data.blankKeepsTheSavedDate')
                : t('data.theFirstDayAScheduledRunMayReadDirectKeepsStatistics', { value1: earliest, value2: historyMonths })
            }
          />
          {editing ? (
            <Alert severity="info" sx={{ mb: 3 }}>{t('data.destinationScheduleAndHistoryBelongToThePipelineNotToThis')}</Alert>
          ) : (
            <>
              <TextField
                select
                label={t('data.destinationWarehouse')}
                value={warehouseId}
                onChange={(e) => setWarehouseId(e.target.value)}
                required
                fullWidth
                sx={{ mb: 2 }}
              >
                {activeWarehouses.map((w) => (
                  <MenuItem key={w.public_id} value={w.public_id}>
                    {w.name}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({w.engine_display})
                    </Typography>
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={t('data.schedule')}
                value={cron}
                onChange={(e) => setCron(e.target.value)}
                fullWidth
                sx={{ mb: 3 }}
              >
                {CRON_PRESETS.map((preset) => (
                  <MenuItem key={preset.value} value={preset.value}>
                    {preset.label}
                  </MenuItem>
                ))}
              </TextField>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={backfill.enabled}
                      onChange={(e) => setBackfill({ ...backfill, enabled: e.target.checked })}
                    />
                  }
                  label={t('data.loadHistoryNow')}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>{t('data.offByDefaultHistoryLoads')}{' '}{BACKFILL_SLICE_DAYS}{' '}{t('data.daysPerRunEverySelectedClientInEachStatisticsCostNo')}{' '}{earliest}{' '}{t('data.on')}</Typography>
                {backfill.enabled && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      type="date"
                      label={t('data.from')}
                      value={backfill.start}
                      onChange={(e) => setBackfill({ ...backfill, start: e.target.value })}
                      error={Boolean(backfill.start) && backfill.start < earliest}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: earliest }}
                    />
                    <TextField
                      type="date"
                      label={t('data.to')}
                      value={backfill.end}
                      onChange={(e) => setBackfill({ ...backfill, end: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      helperText={t('data.exclusiveTheFirstDayNotLoaded')}
                    />
                  </Box>
                )}
                {backfill.enabled && estimate > 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {countLabel(estimate, 'day')}{' '}{t('data.in')}{' '}{countLabel(backfillRuns, 'run')}{' '}{t('data.ofUpTo')}{' '}{BACKFILL_SLICE_DAYS}{' '}{t('data.days3')}{isAgency
                      ? t('data.clientsInEach', { clients: countLabel(logins.length, 'client') })
                      : ''}
                    .
                  </Alert>
                )}
                {queriesCutShort && (
                  <Alert severity="warning" sx={{ mt: 2 }}>{t('data.directKeepsSearchQueriesFor')}{' '}{queryDays}{' '}{t('data.daysTheDaysBefore')}{' '}
                    {queryCutoff}{' '}{t('data.loadWithoutThem')}</Alert>
                )}
              </Paper>
            </>
          )}

          <Button variant="contained" disabled={!canLeaveStep[4] || saving} onClick={save}>
            {saving ? t('data.saving') : editing ? t('data.saveChanges') : t('data.createSource')}
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 4 }}>
        <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)}>{t('data.back')}</Button>
        {activeStep < STEPS().length - 1 && (
          <Button
            variant="contained"
            disabled={!canLeaveStep[activeStep] || probe.isPending}
            onClick={next}
          >{t('data.next')}</Button>
        )}
      </Box>
    </Box>
  );
}
